import { World } from './World';
import { PhysicsEngine } from './physics/PhysicsEngine';
import type { SimulationConfig } from '../config/SimulationConfig';
import type { SimulationState } from '../types/SimulationTypes';

export class Simulation {
  private world: World;
  private physicsEngine: PhysicsEngine;
  private config: SimulationConfig;
  private time: number = 0;

  constructor(config: SimulationConfig) {
    this.config = config;
    this.world = new World(
      config.grid.width,
      config.grid.height,
      config.grid.initialHeight
    );

    // Create beach slope - higher at top, lower at bottom
    this.initializeBeachSlope();

    this.physicsEngine = new PhysicsEngine(config);
  }

  private initializeBeachSlope(): void {
    const { width, height } = this.config.grid;
    const maxHeight = this.config.grid.initialHeight + 8; // Higher ground at top
    const minHeight = Math.max(0, this.config.grid.initialHeight - 8); // Lower at water edge

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        // Create slope from top (high) to bottom (low)
        const normalizedY = y / (height - 1); // 0 at top, 1 at bottom
        const baseHeight = maxHeight - (maxHeight - minHeight) * normalizedY;

        // Add some gentle variation for natural beach feel
        const variation = Math.sin(x * 0.1) * 0.5 + Math.cos(y * 0.15) * 0.5;

        this.world.sandGrid.set(x, y, baseHeight + variation);
      }
    }
  }

  public update(deltaTime: number): void {
    this.time += deltaTime;
    this.physicsEngine.update(
      this.world.sandGrid,
      this.world.waterGrid,
      this.world.wetSandTracker,
      this.world.foamParticles,
      this.world.dustParticles,
      deltaTime,
      this.time
    );
  }

  public modifySand(x: number, y: number, delta: number, brushSize: number = 1): void {
    const radius = Math.floor(brushSize);

    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance <= brushSize) {
          const targetX = Math.floor(x + dx);
          const targetY = Math.floor(y + dy);

          const falloff = 1 - (distance / brushSize);
          const amount = delta * falloff;

          this.world.sandGrid.add(targetX, targetY, amount);
        }
      }
    }
  }

  public addWater(x: number, y: number, amount: number, brushSize: number = 1): void {
    const radius = Math.floor(brushSize);

    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance <= brushSize) {
          const targetX = Math.floor(x + dx);
          const targetY = Math.floor(y + dy);

          const falloff = 1 - (distance / brushSize);
          const volume = amount * falloff;

          this.world.waterGrid.addHeight(targetX, targetY, volume);
        }
      }
    }
  }

  public reset(): void {
    this.world.reset(this.config.grid.initialHeight);
    this.initializeBeachSlope();
    this.time = 0;
  }

  public getState(): SimulationState {
    return {
      sandGrid: this.world.sandGrid,
      waterGrid: this.world.waterGrid,
      wetSandTracker: this.world.wetSandTracker,
      foamParticles: this.world.foamParticles,
      dustParticles: this.world.dustParticles,
      time: this.time,
      metadata: {
        gridWidth: this.config.grid.width,
        gridHeight: this.config.grid.height,
        maxHeight: this.world.sandGrid.getMaxHeight()
      }
    };
  }

  public getConfig(): SimulationConfig {
    return this.config;
  }

  public getPhysicsEngine(): PhysicsEngine {
    return this.physicsEngine;
  }
}
